import { useState, useEffect, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { useAuth } from '../contexts/AuthContext';
import { useDashboard } from '../contexts/DashboardContext';
import { apiFetch } from '../utils/api';
import { teamAcronym } from '../utils/teamAcronyms';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

const NUM_DAYS = 7;

function seededRng(seed) {
  let s = seed;
  return () => {
    s = (s * 16807 + 0) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

function hashStr(str) {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}

function generateTraces(marketOdds, home, away, matchDate) {
  const implied = marketOdds.map((o) => 1 / o);
  const overround = implied.reduce((a, b) => a + b, 0);
  const currentNorm = implied.map((p) => p / overround);

  const rng = seededRng(hashStr(`${home}${away}${matchDate}`));

  const history = [currentNorm.slice()];
  let probs = currentNorm.slice();

  for (let i = 0; i < NUM_DAYS; i++) {
    probs = probs.map((p) => {
      const noise = (rng() - 0.5) * 0.04;
      return Math.max(0.02, p + noise);
    });
    const sum = probs.reduce((a, b) => a + b, 0);
    probs = probs.map((p) => p / sum);
    history.push(probs.slice());
  }

  history.reverse();

  const endDate = new Date(matchDate + 'T00:00:00');
  const labels = history.map((_, i) => {
    const d = new Date(endDate);
    d.setDate(d.getDate() - (NUM_DAYS - i));
    return `${d.getDate()}/${d.getMonth() + 1}`;
  });

  return {
    labels,
    traces: [
      { name: home, data: history.map((h) => h[0] * overround) },
      { name: 'Draw', data: history.map((h) => h[1] * overround) },
      { name: away, data: history.map((h) => h[2] * overround) },
    ],
    overround,
  };
}

const COLORS = ['#0d6efd', '#6c757d', '#dc3545'];

export default function MatchPriceChartCard() {
  const { token } = useAuth();
  const { selectedTeam, selectedDate, modelDatesParam } = useDashboard();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token || !selectedTeam || !selectedDate) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams({ team: selectedTeam, date: selectedDate });
    if (modelDatesParam) params.set('modelDates', modelDatesParam);
    apiFetch(`/match-pricing?${params}`, token)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [token, selectedTeam, selectedDate, modelDatesParam]);

  const chartResult = useMemo(() => {
    if (!data?.marketOdds || !data.home || !data.away || !data.matchDate) return null;
    return generateTraces(data.marketOdds, data.home, data.away, data.matchDate);
  }, [data]);

  const chartData = useMemo(() => {
    if (!chartResult) return null;
    return {
      labels: chartResult.labels,
      datasets: chartResult.traces.map((trace, i) => ({
        label: teamAcronym(trace.name),
        data: trace.data,
        borderColor: COLORS[i],
        backgroundColor: COLORS[i],
        borderWidth: 2,
        pointRadius: 2,
        pointHoverRadius: 4,
        tension: 0.3,
      })),
    };
  }, [chartResult]);

  const chartOptions = useMemo(() => {
    let yMin = 0;
    let yMax;
    if (chartResult) {
      const allValues = chartResult.traces.flatMap((t) => t.data);
      const dataMin = Math.min(...allValues);
      const dataMax = Math.max(...allValues);
      const range = dataMax - dataMin;
      const padding = Math.max(range * 0.15, 0.01);
      yMin = Math.max(0, dataMin - padding);
      yMax = dataMax + padding;
    }
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: true, position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } },
        tooltip: {
          callbacks: {
            label: (ctx) => `${ctx.dataset.label}: ${(ctx.parsed.y * 100).toFixed(1)}%`,
          },
        },
      },
      scales: {
        x: { ticks: { font: { size: 10 } } },
        y: {
          ticks: {
            font: { size: 10 },
            callback: (v) => `${(v * 100).toFixed(0)}%`,
          },
          min: yMin,
          ...(yMax !== undefined && { max: yMax }),
        },
      },
    };
  }, [chartResult]);

  const noMarketOdds = !loading && !error && data && !data.noMatch && !data.marketOdds;

  return (
    <div className="card h-100">
      <div className="card-header d-flex align-items-center">
        <strong>Match Odds Trend</strong>
        <span className="ms-auto text-muted small">Simulated</span>
      </div>
      <div className="card-body">
        {loading && (
          <div className="text-center py-4"><div className="spinner-border spinner-border-sm" /></div>
        )}
        {error && (
          <div className="text-center py-4 text-danger small">{error}</div>
        )}
        {!loading && !error && data?.noMatch && (
          <div className="text-center py-4 text-muted small">No upcoming match within 7 days</div>
        )}
        {noMarketOdds && (
          <div className="text-center py-4 text-muted small">No market odds available</div>
        )}
        {!loading && !error && chartData && (
          <div style={{ height: '220px' }}>
            <Line data={chartData} options={chartOptions} />
          </div>
        )}
      </div>
    </div>
  );
}
